/** Build a tree from products, events, and streams — no string parsing for grouping */

import type { StreamData, EventData, ProductData } from "./api";

export interface TreeNode {
    label: string;
    key: string;
    children: TreeNode[];
    streams?: StreamData[];
    meta?: { totalSize: number; totalDuration: number; totalStored: number; totalChunks: number; pct: number };
}

/** Extract "Day N" from an event title, or "" if not found */
function parseDay(title: string | null): string {
    if (!title) return "";
    const m = title.match(/Day\s*(\d+)/i);
    return m ? `Day ${m[1]}` : "";
}

/** Extract the stage/hall name from an event title (part after "Day N - ") */
function parseStage(title: string | null): string {
    if (!title) return "Unknown";
    const m = title.match(/Day\s*\d+\s*[-–]\s*(.+)/i);
    return m ? m[1].trim().replace(/\s+/g, " ") : title.replace(/\s+/g, " ").trim();
}

/** Build the tree: year → product → day → event → streams */
export function buildTree(
    streams: StreamData[],
    products: ProductData[],
    events: EventData[],
): TreeNode[] {
    const productMap = new Map(products.map(p => [p.id, p]));
    const eventMap = new Map(events.map(e => [e.id, e]));

    // Index streams by event — match via page_url or stream_key parsing
    const streamsByEvent = new Map<number, StreamData[]>();
    const orphanStreams: StreamData[] = [];

    // Build a lookup: event page_url → event id, and event slug → event (per product)
    const eventByUrl = new Map<string, EventData>();
    const eventByProductSlug = new Map<string, EventData>(); // key: "productId:slug"
    for (const e of events) {
        if (e.page_url) {
            try { eventByUrl.set(new URL(e.page_url).pathname, e); } catch {}
        }
        eventByProductSlug.set(`${e.product_id}:${e.slug}`, e);
    }

    for (const s of streams) {
        // Skip audio streams — they're linked to their parent video stream
        if (s.stream_key.endsWith("-audio")) continue;

        let matched: EventData | undefined;

        // Try matching by page_url
        if (s.page_url) {
            try {
                matched = eventByUrl.get(new URL(s.page_url).pathname);
            } catch {}
        }

        // Try matching by event_id (vimeo ID) — find event with matching slug derived from stream_key
        if (!matched && s.product_id) {
            const slug = s.stream_key.replace(/^dvr-/, "").replace(/-\d+$/, "");
            matched = eventByProductSlug.get(`${s.product_id}:${slug}`);
        }

        if (matched) {
            if (!streamsByEvent.has(matched.id)) streamsByEvent.set(matched.id, []);
            streamsByEvent.get(matched.id)!.push(s);
        } else {
            orphanStreams.push(s);
        }
    }

    // Build tree: year → product → day → event → streams
    // Group events by product, then by day
    const years = new Map<number | string, Map<number, { product: ProductData; days: Map<string, { events: Map<number, { event: EventData; streams: StreamData[] }> }> }>>();

    for (const [eventId, eventStreams] of streamsByEvent) {
        const event = eventMap.get(eventId);
        if (!event) continue;
        const product = productMap.get(event.product_id);
        if (!product) continue;

        const year = product.year || "unknown";
        if (!years.has(year)) years.set(year, new Map());
        const productsInYear = years.get(year)!;
        if (!productsInYear.has(product.id)) productsInYear.set(product.id, { product, days: new Map() });
        const prodEntry = productsInYear.get(product.id)!;

        const day = parseDay(event.title) || "Other";
        if (!prodEntry.days.has(day)) prodEntry.days.set(day, { events: new Map() });
        const dayEntry = prodEntry.days.get(day)!;

        dayEntry.events.set(eventId, { event, streams: eventStreams });
    }

    // Handle orphan streams (no matching event) — group under product if possible
    for (const s of orphanStreams) {
        const product = s.product_id ? productMap.get(s.product_id) : undefined;
        const year = product?.year || "unknown";
        const title = s.title || s.stream_key;

        if (!years.has(year)) years.set(year, new Map());
        const productsInYear = years.get(year)!;

        const prodId = product?.id || -1;
        if (!productsInYear.has(prodId)) {
            productsInYear.set(prodId, {
                product: product || { id: -1, slug: "unknown", url: "", label: "Unknown", year: null, description: null },
                days: new Map(),
            });
        }
        const prodEntry = productsInYear.get(prodId)!;

        const day = parseDay(title) || "Other";
        if (!prodEntry.days.has(day)) prodEntry.days.set(day, { events: new Map() });
        const dayEntry = prodEntry.days.get(day)!;

        // Create a synthetic event entry for orphans (negative ID to avoid collisions)
        const syntheticId = -(s.id);
        dayEntry.events.set(syntheticId, {
            event: { id: syntheticId, product_id: prodId, slug: s.stream_key, page_url: s.page_url, title, thumb_url: null, first_seen_at: s.created_at, live_seen_at: null, dvr_seen_at: null },
            streams: [s],
        });
    }

    // Convert to TreeNode[]
    const tree: TreeNode[] = [];

    const sortedYears = [...years.entries()].sort((a, b) => String(b[0]).localeCompare(String(a[0])));

    for (const [year, productsInYear] of sortedYears) {
        const yearNode: TreeNode = { label: String(year), key: `y-${year}`, children: [], streams: [] };

        for (const [, { product, days }] of productsInYear) {
            const prodLabel = product.label || product.slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
            const prodNode: TreeNode = { label: prodLabel, key: `p-${year}-${product.id}`, children: [], streams: [] };

            const sortedDays = [...days.entries()].sort((a, b) => {
                const na = parseInt(a[0].replace(/\D/g, "")) || 0;
                const nb = parseInt(b[0].replace(/\D/g, "")) || 0;
                return na - nb;
            });

            for (const [day, { events: dayEvents }] of sortedDays) {
                const dayNode: TreeNode = { label: day, key: `d-${year}-${product.id}-${day}`, children: [], streams: [] };

                // Sort events: results last, then alphabetical by stage
                const sortedEvents = [...dayEvents.entries()].sort((a, b) => {
                    const stageA = parseStage(a[1].event.title);
                    const stageB = parseStage(b[1].event.title);
                    const aResult = stageA.toLowerCase().includes("result");
                    const bResult = stageB.toLowerCase().includes("result");
                    if (aResult && !bResult) return 1;
                    if (!aResult && bResult) return -1;
                    return stageA.localeCompare(stageB);
                });

                for (const [, { event, streams: eventStreams }] of sortedEvents) {
                    const stage = parseStage(event.title);

                    // Sort streams: live first, then dvr
                    eventStreams.sort((a, b) => {
                        if (a.type === "live" && b.type !== "live") return -1;
                        if (a.type !== "live" && b.type === "live") return 1;
                        return 0;
                    });

                    if (eventStreams.length === 1) {
                        // Single stream — leaf node
                        const s = eventStreams[0];
                        const label = s.type === "dvr" ? `${stage} (DVR)` : stage;
                        dayNode.streams!.push(s);
                        dayNode.children.push({
                            label,
                            key: s.stream_key,
                            children: [],
                            streams: [s],
                        });
                    } else {
                        // Multiple streams (live + DVR) — event group
                        const eventNode: TreeNode = {
                            label: stage,
                            key: `e-${event.product_id}-${event.slug}`,
                            children: [],
                            streams: eventStreams,
                        };
                        for (const s of eventStreams) {
                            const variantLabel = s.type === "dvr" ? `${stage} (DVR)` : stage;
                            eventNode.children.push({
                                label: variantLabel,
                                key: s.stream_key,
                                children: [],
                                streams: [s],
                            });
                        }
                        dayNode.streams!.push(...eventStreams);
                        dayNode.children.push(eventNode);
                    }
                }

                prodNode.children.push(dayNode);
            }

            prodNode.streams = prodNode.children.flatMap(d => d.streams || []);
            yearNode.children.push(prodNode);
        }

        yearNode.streams = yearNode.children.flatMap(p => p.streams || []);
        tree.push(yearNode);
    }

    return tree;
}

export function fmtBytes(n: number): string {
    if (n >= 1e9) return (n / 1e9).toFixed(1) + " GB";
    if (n >= 1e6) return (n / 1e6).toFixed(0) + " MB";
    return (n / 1e3).toFixed(0) + " KB";
}

export function fmtDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}
